import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Hotel,
  MapPin,
  BedDouble,
  Users,
  Mail,
  Phone,
  Hash,
  CalendarCheck,
  AlertCircle,
  Loader2,
  Printer,
  Navigation,
  User,
  IndianRupee 
} from "lucide-react";

const API_USERTICKET_URL =
  process.env.REACT_APP_API_USERTICKET_URL || "http://localhost:50574";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const firstImage = (img) => {
  if (!img) return "";
  return String(img).split(",")[0].trim();
};

const show = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const formatPrice = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n)
    ? v
    : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const TERMS = [
  "Please carry a valid government-issued photo ID for every guest at the time of check-in.",
  "Standard check-in time is 12:00 PM and check-out time is 11:00 AM, unless the hotel specifies otherwise. Early check-in or late check-out is subject to availability and may incur extra charges.",
  "The primary guest named on this voucher must be present at check-in. The number of guests must not exceed the occupancy booked.",
  "Cancellation and refund are governed by the hotel's cancellation policy applicable at the time of booking. Charges, if any, will be deducted as per that policy.",
  "No-show or early departure may result in forfeiture of the booking amount.",
  "Any additional services such as meals, laundry, or extra beds not included in the booking must be paid directly at the hotel.",
  "The hotel reserves the right of admission. Damage to hotel property will be charged to the guest.",
  "This voucher is system generated and valid only for the booking details mentioned above.",
];
export default function UserTicket() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const bookingId = params.get("bookingId");
  const fromDate = params.get("fromDate");
  const toDate = params.get("toDate");

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!bookingId) {
        setError("Booking ID is missing.");
        setLoading(false);
        return;
      }

      // dates URL mein na ho to last 1 year ka range lagao
      const today = new Date();
      const past = new Date();
      past.setFullYear(past.getFullYear() - 1);
      const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

      const qs = new URLSearchParams({
        FromDate: fromDate || toISO(past),
        ToDate: toDate || toISO(today),
        BookingId: bookingId,
      });

      try {
      const res = await fetch(`${API_USERTICKET_URL}/api/hotel/GetTicketByBookingId`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      FromDate: fromDate || toISO(past),
      ToDate: toDate || toISO(today),
      BookingId: bookingId,
    }),
  });
  const json = await res.json();

        if (cancelled) return;

        if (!res.ok || json.status !== "success") {
          setError(json.message || "Ticket not found.");
        } else {
          setTicket(json.data);
        }
      } catch (err) {
        console.error("GetTicketByBookingId error:", err);
        if (!cancelled) setError("Network error. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [bookingId, fromDate, toDate]);

  const img = firstImage(ticket?.HotelImage);
  const hasMap = ticket?.Latitude && ticket?.Longitude;
  const totalGuests =
    Number(ticket?.NumberOfAdults || 0) + Number(ticket?.NumberOfChild || 0);

  return (
    <>
      <HTStyle />
      <div className="ht-page">
        <div className="ht-bar ht-noprint">
          <button type="button" className="ht-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={15} /> Back
          </button>
          {ticket && (
            <button type="button" className="ht-print" onClick={() => window.print()}>
              <Printer size={15} /> Print ticket
            </button>
          )}
        </div>

        {loading && (
          <div className="ht-state">
            <Loader2 size={30} className="ht-spin" />
            <p>Loading your ticket…</p>
          </div>
        )}

        {!loading && error && (
          <div className="ht-state ht-state--error">
            <AlertCircle size={30} />
            <h3>Ticket not available</h3>
            <p>{error}</p>
          </div>
        )}

        {!loading && ticket && (
          <div className="ht-ticket">
            <div className="ht-head">
              <div className="ht-head__left">
                <div className="ht-head__icon">
                  <Hotel size={22} />
                </div>
                <div>
                  <p className="ht-head__kicker">Hotel Booking Voucher</p>
                  <h1 className="ht-head__title">{show(ticket.HotelName)}</h1>
                </div>
              </div>
              <span className="ht-status">CONFIRMED</span>
            </div>

            {img && (
              <img
                className="ht-cover"
                src={img}
                alt={ticket.HotelName || "Hotel"}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}

            <div className="ht-body">
              <section className="ht-sec">
                <h2 className="ht-sec__title">Booking details</h2>
                <div className="ht-grid">
                  <Item icon={<Hash size={16} />} label="Booking ID" value={ticket.BookingId} />
                  <Item icon={<Hash size={16} />} label="Transaction ID" value={ticket.TransactionId} />
                  <Item icon={<CalendarCheck size={16} />} label="Booked on" value={formatDate(ticket.BookingDate)} />
                  <Item icon={<BedDouble size={16} />} label="Rooms" value={ticket.NumberOfRooms} />
                  <Item icon={<Users size={16} />} label="Adults" value={ticket.NumberOfAdults} />
                  <Item icon={<Users size={16} />} label="Children" value={ticket.NumberOfChild} />
                  <Item icon={<Users size={16} />} label="Total guests" value={totalGuests} />
                  <Item icon={<CalendarCheck size={16} />} label="Check-in" value={formatDate(ticket.CheckInDate)} />
<Item icon={<CalendarCheck size={16} />} label="Check-out" value={formatDate(ticket.CheckOutDate)} />
<Item icon={<BedDouble size={16} />} label="Room type" value={ticket.RoomType} />
                </div>
              </section>

              <section className="ht-sec">
                <h2 className="ht-sec__title">Hotel</h2>
                <p className="ht-addr">
                  <MapPin size={16} /> {show(ticket.HotelAddress)}
                </p>
                {ticket.HotelDetail && <p className="ht-detail">{ticket.HotelDetail}</p>}
                {hasMap && (
                  <a
                    className="ht-map ht-noprint"
                    href={`https://www.google.com/maps?q=${ticket.Latitude},${ticket.Longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Navigation size={14} /> View on map
                  </a>
                )}
              </section>

              <section className="ht-sec">
                <h2 className="ht-sec__title">Guest</h2>
                <div className="ht-grid">
                  <Item icon={<User size={16} />} label="Name" value={ticket.CustomerName} />
                  <Item icon={<Mail size={16} />} label="Email" value={ticket.CustomerEmail} />
                  <Item icon={<Phone size={16} />} label="Mobile" value={ticket.CustomerMobile} />
                  <Item icon={<MapPin size={16} />} label="Address" value={ticket.CustomerAddress} />
                </div>
              </section>
              <section className="ht-sec">
  <h2 className="ht-sec__title">Payment</h2>
  <div className="ht-price">
    <span className="ht-price__label">
      <IndianRupee size={16} /> Total amount
    </span>
    <strong className="ht-price__amt">{formatPrice(ticket.TotalPrice)}</strong>
  </div>
</section>
<section className="ht-sec">
  <h2 className="ht-sec__title">Terms &amp; Conditions</h2>
  <ol className="ht-terms">
    {TERMS.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ol>
</section>
            </div>

            <div className="ht-foot">
              Please carry a valid photo ID at check-in. This voucher is system generated.
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Item({ icon, label, value }) {
  return (
    <div className="ht-item">
      <span className="ht-item__icon">{icon}</span>
      <div>
        <span className="ht-item__label">{label}</span>
        <strong>{show(value)}</strong>
      </div>
    </div>
  );
}

function HTStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

      .ht-page, .ht-page *, .ht-page *::before, .ht-page *::after { box-sizing: border-box; }
      .ht-page {
        --ink: #16215C; --ink-soft: #26389E; --accent: #E4127F; --orange: #ea580c;
        --paper: #F4F6FB; --line: #E1E5F2; --text: #1A1F36; --text-soft: #667085;
        min-height: 100vh; background: var(--paper);
        font-family: 'Inter', sans-serif; color: var(--text);
        padding: 20px 16px 60px;
      }

      .ht-bar { max-width: 820px; margin: 0 auto 16px; display: flex; justify-content: space-between; gap: 10px; }
      .ht-back, .ht-print {
        display: inline-flex; align-items: center; gap: 6px;
        border-radius: 999px; padding: 8px 16px;
        font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
      }
      .ht-back { background: #fff; color: var(--ink-soft); border: 1px solid var(--line); }
      .ht-print { background: var(--orange); color: #fff; border: none; box-shadow: 0 6px 16px rgba(234,88,12,0.25); }
      .ht-print:hover { background: #B80E64; }

      .ht-state { max-width: 820px; margin: 60px auto; text-align: center; color: var(--text-soft); }
      .ht-state h3 { font-family: 'Fraunces', serif; color: var(--text); margin: 10px 0 4px; }
      .ht-state--error svg { color: #B91C1C; }
      .ht-spin { animation: ht-spin 0.8s linear infinite; color: var(--ink-soft); }
      @keyframes ht-spin { to { transform: rotate(360deg); } }

      .ht-ticket {
        max-width: 820px; margin: 0 auto; background: #fff;
        border: 1px solid var(--line); border-radius: 18px; overflow: hidden;
        box-shadow: 0 10px 30px rgba(22,33,92,0.09);
      }

      .ht-head {
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
        padding: 22px 26px; color: #fff;
        background: linear-gradient(120deg, #0d2b6b 0%, #1a4fa0 55%, #2a63c4 100%);
      }
      .ht-head__left { display: flex; align-items: center; gap: 14px; min-width: 0; }
      .ht-head__icon {
        width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.28);
      }
      .ht-head__kicker { margin: 0; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.75); }
      .ht-head__title { margin: 2px 0 0; font-family: 'Fraunces', serif; font-size: 24px; font-weight: 600; line-height: 1.2; overflow-wrap: anywhere; }
      .ht-status {
        flex-shrink: 0; background: #16a34a; color: #fff;
        font-size: 12px; font-weight: 700; letter-spacing: 0.6px;
        padding: 6px 14px; border-radius: 999px;
      }

      .ht-cover { width: 100%; max-height: 280px; object-fit: cover; display: block; }

      .ht-body { padding: 8px 26px 4px; }
      .ht-sec { padding: 18px 0; border-bottom: 1px dashed var(--line); }
      .ht-sec:last-child { border-bottom: none; }
      .ht-sec__title {
        margin: 0 0 14px; font-family: 'Fraunces', serif; font-size: 17px; font-weight: 600; color: var(--ink);
      }

      .ht-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px 18px; }
      .ht-item { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
      .ht-item__icon { color: var(--ink-soft); margin-top: 2px; flex-shrink: 0; }
      .ht-item div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .ht-item__label { font-size: 11px; color: var(--text-soft); text-transform: uppercase; letter-spacing: 0.4px; }
      .ht-item strong { font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }

      .ht-addr { display: flex; gap: 8px; margin: 0; font-size: 14px; line-height: 1.5; }
      .ht-addr svg { color: var(--accent); flex-shrink: 0; margin-top: 3px; }
      .ht-detail { margin: 10px 0 0; font-size: 13px; color: var(--text-soft); line-height: 1.55; }
      .ht-map {
        display: inline-flex; align-items: center; gap: 6px; margin-top: 12px;
        font-size: 13px; font-weight: 600; color: var(--ink-soft); text-decoration: none;
      }
      .ht-map:hover { text-decoration: underline; }

      .ht-foot {
        padding: 14px 26px; background: #F8F9FD; border-top: 1px solid var(--line);
        font-size: 12px; color: var(--text-soft); text-align: center;
      }

      @media (max-width: 640px) {
        .ht-head { padding: 18px; }
        .ht-head__title { font-size: 19px; }
        .ht-body { padding: 4px 18px; }
        .ht-grid { grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 400px) {
        .ht-grid { grid-template-columns: 1fr; }
      }

     @media print {
  /* ticket ke alawa sab kuch hide */
  body * { visibility: hidden !important; }
  .ht-ticket, .ht-ticket * { visibility: visible !important; }

  /* ticket ko page ke top-left par rakho taaki header ki jagah khaali na chhoote */
  .ht-ticket {
    position: absolute; left: 0; top: 0;
    width: 100%; max-width: 100%;
    box-shadow: none; border: none; border-radius: 0;
  }

  .ht-noprint { display: none !important; }
  .ht-page { background: #fff; padding: 0; min-height: auto; }
  .ht-head { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .ht-status { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { margin: 10mm; }
} 
  .ht-price {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  background: #F8F9FD; border: 1px dashed var(--line); border-radius: 12px;
  padding: 16px 20px;
}
.ht-price__label { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: var(--text-soft); font-weight: 600; }
.ht-price__amt { font-family: 'Fraunces', serif; font-size: 26px; font-weight: 600; color: var(--ink); }
.ht-terms { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 8px; }
.ht-terms li { font-size: 12.5px; line-height: 1.55; color: var(--text-soft); }
    `}</style>
  );
}